/**
 * Timezone Helper Utility
 *
 * Centralized utility for timezone-aware date formatting in the AKONGO Animal Welfare Monitor.
 * All components should use this utility instead of direct moment timezone calls.
 *
 * @module utils/timezoneHelper
 */

import moment from "moment-timezone";

/**
 * Default timezone used when zoo has no timezone configured
 * @constant {string}
 */
export const DEFAULT_TIMEZONE = "Europe/Paris";

/**
 * Extract timezone from zoo object with fallback to default
 *
 * @param {Object} zoo - Zoo document object
 * @returns {string} IANA timezone string
 *
 * @example
 * const tz = getZooTimezone(zoo); // "America/New_York" or "Europe/Paris"
 */
export const getZooTimezone = (zoo) => {
  const tz = zoo?.timezone;

  // Validate timezone exists in moment-timezone
  if (tz && moment.tz.zone(tz)) {
    return tz;
  }

  // Log warning for invalid timezone (only if one was set)
  if (tz) {
    console.warn(`Invalid timezone "${tz}" for zoo, falling back to default`);
  }

  return DEFAULT_TIMEZONE;
};

/**
 * Fuseau d'un parc pour les bornes de jour des vues V2 : lit `timezone` puis
 * `timeZone`, ignore les valeurs vides ou inconnues, et se replie sur
 * Europe/Paris — jamais sur UTC (au Pal, `timezone` vaut "" : un repli UTC
 * décalait « Aujourd'hui » d'un jour entre 0 h et 2 h, recette 12.1.0).
 * Accepte aussi directement une chaîne IANA.
 *
 * @param {Object|string|null} zoo - Doc parc, session V2 ({ timezone }) ou fuseau
 * @returns {string} IANA timezone string
 */
export const resolveZooTimezone = (zoo) => {
  const candidates = typeof zoo === "string" ? [zoo] : [zoo?.timezone, zoo?.timeZone];
  const valid = candidates.find((tz) => typeof tz === "string" && tz.trim() && moment.tz.zone(tz.trim()));
  return valid ? valid.trim() : DEFAULT_TIMEZONE;
};

/**
 * Normalize any date-like value to a moment object
 *
 * Handles:
 * - Date objects
 * - moment objects
 * - Firestore Timestamp objects (with toDate method)
 * - Unix timestamps in seconds
 * - Unix timestamps in milliseconds
 *
 * @param {*} date - Date value to normalize
 * @returns {moment.Moment|null} Moment object or null if invalid
 */
const normalizeDate = (date) => {
  if (!date) return null;

  // Already a moment object
  if (moment.isMoment(date)) {
    return date;
  }

  // Firestore Timestamp (has toDate method)
  if (date?.toDate && typeof date.toDate === "function") {
    return moment(date.toDate());
  }

  // Unix timestamp in seconds (before year 2286)
  if (typeof date === "number" && date < 10000000000) {
    return moment.unix(date);
  }

  // Date object, milliseconds timestamp, or ISO string
  return moment(date);
};

/**
 * Format any date-like value in zoo's timezone
 *
 * @param {Date|moment|Timestamp|number} date - Date to format
 * @param {Object} zoo - Zoo document object
 * @param {string} [format="DD/MM/YYYY"] - Moment format string
 * @returns {string} Formatted date string, empty string if date is invalid
 *
 * @example
 * formatDateInZoo(observation.timestamp, zoo) // "01/02/2026"
 * formatDateInZoo(task.deadline, zoo, "DD MMM YYYY") // "01 Feb 2026"
 */
export const formatDateInZoo = (date, zoo, format = "DD/MM/YYYY", locale) => {
  const m = normalizeDate(date);

  if (!m || !m.isValid()) {
    if (date) {
      console.warn("Invalid date passed to formatDateInZoo:", date);
    }
    return "";
  }

  const tz = getZooTimezone(zoo);
  const localized = locale ? m.locale(locale) : m;
  return localized.tz(tz).format(format);
};

/**
 * Format datetime in zoo's timezone (convenience method)
 *
 * @param {Date|moment|Timestamp|number} date - Date to format
 * @param {Object} zoo - Zoo document object
 * @param {string} [format="DD/MM/YYYY HH:mm"] - Moment format string
 * @returns {string} Formatted datetime string
 *
 * @example
 * formatDateTimeInZoo(task.createdAt, zoo) // "01/02/2026 14:30"
 */
export const formatDateTimeInZoo = (date, zoo, format = "DD/MM/YYYY HH:mm") => {
  return formatDateInZoo(date, zoo, format);
};

/**
 * Format a date as a short absolute date in zoo's timezone
 *
 * L'année n'est ajoutée que si la date ne tombe pas dans l'année courante :
 * une note de l'an dernier affichée « 25/07 » serait ambiguë.
 *
 * @param {Date|moment|Timestamp|number} date - Date to format
 * @param {Object} zoo - Zoo document object
 * @param {Object} [options]
 * @param {boolean} [options.withTime=false] - Append "HH:mm"
 * @returns {string} "25/07", "25/07/2025", "25/07 14:30"… ("" si date invalide)
 *
 * @example
 * formatShortDateInZoo(note.timestamp, zoo) // "25/07" (année courante)
 * formatShortDateInZoo(note.timestamp, zoo) // "25/07/2025" (année passée)
 */
export const formatShortDateInZoo = (date, zoo, { withTime = false } = {}) => {
  const m = normalizeDate(date);
  if (!m || !m.isValid()) return "";

  const tz = getZooTimezone(zoo);
  const zoned = m.clone().tz(tz);
  const sameYear = zoned.year() === moment().tz(tz).year();

  let format = sameYear ? "DD/MM" : "DD/MM/YYYY";
  if (withTime) format += " HH:mm";

  return zoned.format(format);
};

/**
 * Format a date as relative time followed by its short absolute date
 *
 * Aligné sur le mobile (`fromNowWithDateInZoo`) : le relatif situe l'événement
 * d'un coup d'œil, la date absolue lève l'ambiguïté (demande Le Pal 30/07/2026).
 *
 * @param {Date|moment|Timestamp|number} date - Date to format
 * @param {Object} zoo - Zoo document object
 * @param {Object} [options]
 * @param {string} [options.locale] - Locale moment pour le relatif (défaut : locale globale)
 * @param {string} [options.separator=" · "] - Séparateur relatif / date
 * @returns {string} "il y a 12 jours · 25/07" ("" si date invalide)
 */
export const fromNowWithDateInZoo = (
  date,
  zoo,
  { locale, separator = " · " } = {}
) => {
  const m = normalizeDate(date);
  if (!m || !m.isValid()) return "";

  const tz = getZooTimezone(zoo);
  const zoned = m.clone().tz(tz);
  const relative = (locale ? zoned.clone().locale(locale) : zoned).fromNow();
  const shortDate = formatShortDateInZoo(date, zoo);

  if (!relative) return shortDate;
  if (!shortDate) return relative;

  return `${relative}${separator}${shortDate}`;
};

/**
 * Get current moment in zoo's timezone
 *
 * @param {Object} zoo - Zoo document object
 * @returns {moment.Moment} Moment object set to current time in zoo's timezone
 *
 * @example
 * const now = nowInZoo(zoo);
 * const today = nowInZoo(zoo).startOf('day');
 */
export const nowInZoo = (zoo) => {
  const tz = getZooTimezone(zoo);
  return moment().tz(tz);
};

/**
 * Parse a date string as being in zoo's timezone
 *
 * Useful for interpreting user input from forms
 *
 * @param {string} dateString - Date string to parse
 * @param {Object} zoo - Zoo document object
 * @param {string} [format="DD/MM/YYYY HH:mm"] - Expected format of the date string
 * @returns {moment.Moment} Moment object in zoo's timezone
 *
 * @example
 * const m = parseInZoo("01/02/2026 14:30", zoo);
 * const utc = m.utc(); // Convert to UTC for storage
 */
export const parseInZoo = (dateString, zoo, format = "DD/MM/YYYY HH:mm") => {
  const tz = getZooTimezone(zoo);
  return moment.tz(dateString, format, tz);
};

/**
 * Convert a date from zoo timezone to UTC
 *
 * Use this when saving user input to Firestore
 *
 * @param {Date} date - Date object (as selected in local/zoo time)
 * @param {Object} zoo - Zoo document object
 * @returns {Date} Date object in UTC
 *
 * @example
 * const utcDate = toUTCFromZoo(selectedDate, zoo);
 * await firestore.collection('tasks').add({ deadline: utcDate });
 */
export const toUTCFromZoo = (date, zoo) => {
  if (!date) return null;
  const tz = getZooTimezone(zoo);
  // Interpret the date as being in zoo timezone, then convert to UTC
  return moment.tz(date, tz).utc().toDate();
};

/**
 * Convert a UTC date to zoo timezone for display
 *
 * Use this when displaying a date from Firestore in a date picker
 *
 * @param {Date|Timestamp} date - Date in UTC
 * @param {Object} zoo - Zoo document object
 * @returns {Date} Date object adjusted to zoo timezone
 *
 * @example
 * const displayDate = toZooTimeFromUTC(task.deadline, zoo);
 * <DatePicker selected={displayDate} />
 */
export const toZooTimeFromUTC = (date, zoo) => {
  const m = normalizeDate(date);
  if (!m || !m.isValid()) return null;

  const tz = getZooTimezone(zoo);
  return m.tz(tz).toDate();
};
